import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface AuditLog {
  id: number;
  admin_user_id?: number;
  admin_user_email?: string;
  action: string;
  action_type: string;
  target_type: string;
  target_id: number;
  target_name?: string;
  reason?: string;
  confirmed: boolean;
  created_at: string;
}

interface AuditLogsGridProps {
  auditLogs: AuditLog[];
  searchValue?: string;
}

const AuditLogsGrid = forwardRef<any, AuditLogsGridProps>(
  ({ auditLogs, searchValue = '' }, ref) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter audit logs based on search value
    const filteredAuditLogs = React.useMemo(() => {
      if (!searchValue.trim()) return auditLogs;
      const lowerSearch = searchValue.toLowerCase();
      return auditLogs.filter((log) => {
        const searchFields = [
          log.action,
          log.action_type,
          log.target_type,
          log.target_name,
          log.admin_user_email,
          log.reason,
          log.id?.toString(),
          log.target_id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [auditLogs, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'action',
          headerName: 'Action',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'action_type',
          headerName: 'Action Type',
          width: 120,
          sortable: true,
          filter: true,
        },
        {
          field: 'target_type',
          headerName: 'Target Type',
          width: 120,
          sortable: true,
          filter: true,
        },
        {
          field: 'target_name',
          headerName: 'Target',
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            return params.value || `ID: ${params.data.target_id}`;
          },
        },
        {
          field: 'admin_user_email',
          headerName: 'Admin',
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            return (
              params.value || `ID: ${params.data.admin_user_id || 'Unknown'}`
            );
          },
        },
        {
          field: 'reason',
          headerName: 'Reason',
          flex: 2,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const reason = params.value || '';
            return reason.length > 50
              ? `${reason.substring(0, 50)}...`
              : reason || '-';
          },
        },
        {
          field: 'confirmed',
          headerName: 'Confirmed',
          width: 100,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const confirmed = params.value;
            return (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: confirmed
                    ? `${palette.cyanLimeGreen}20`
                    : `${palette.darkGrayBlue}20`,
                  color: confirmed
                    ? palette.cyanLimeGreen
                    : palette.darkGrayBlue,
                  border: `1px solid ${confirmed ? palette.cyanLimeGreen : palette.darkGrayBlue}40`,
                  fontSize: '11px',
                  fontWeight: '600',
                  width: 'fit-content',
                }}
              >
                {confirmed ? '✓ Yes' : 'No'}
              </div>
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 150,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
      ],
      []
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredAuditLogs}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

AuditLogsGrid.displayName = 'AuditLogsGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default AuditLogsGrid;
